import { ProvisionType } from "@redotech/cockroachdb-codegen/congodb/provision-type";
import { UpsellBillingTrigger } from "@redotech/cockroachdb-codegen/congodb/upsell-billing-trigger";
import { Currency } from "@redotech/money/currencies";
import { isDraftingExchangeReturn } from "@redotech/redo-return-core-common/draft-return/draft-return-predicates";
import { chargesExchangeShippingViaStripe } from "@redotech/redo-return-core-common/returns/return-totals/exchange-shipping-billing";
import { RepairFeeCollectionStrategy } from "@redotech/redo-return-warranties-common/warranties";
import type { DraftReturn, ReturnTotalCalculations } from "./draft-return";
import { getBaseRepairFee } from "./util";
const STRIPE_MINIMUM_CHARGE_USD = 0.5;

function shouldWaiveFee(amount: number, currency: Currency): boolean {
  return currency === Currency.USD && amount < STRIPE_MINIMUM_CHARGE_USD;
}

/**
 * True when the outstanding balance is billed on the exchange invoice rather
 * than charged at submit — the only case Shopify store credit can cover.
 */
export function isBalanceInvoicedLater(
  upsellBillingTrigger: UpsellBillingTrigger | null | undefined,
): boolean {
  return (
    upsellBillingTrigger != null &&
    upsellBillingTrigger !== UpsellBillingTrigger.ReturnCreated
  );
}

export function shouldCollectPayment(
  draftReturn: DraftReturn,
  calculations: ReturnTotalCalculations,
  options: { forceExchangeStripeBilling?: boolean } = {},
): boolean {
  const {
    totalShippingFee,
    exchangeShippingFee,
    totalRepairFee,
    skipPayment,
    labelDeducted,
    totalPaymentOwed,
  } = calculations;

  // Regular and exchange shipping fees follow separate billing strategies.
  const regularShippingFee = totalShippingFee - exchangeShippingFee;
  const needsToPayRegularShippingFee =
    regularShippingFee > 0 && !skipPayment && !labelDeducted;

  // Prefer the calculator decision, then derive it for legacy callers.
  const chargeExchangeShippingViaStripe =
    calculations.chargesExchangeShippingViaStripe ??
    chargesExchangeShippingViaStripe({
      upsellBillingTrigger: draftReturn.upsellBillingTrigger,
      forceExchangeStripeBilling: options.forceExchangeStripeBilling,
    });
  const needsToPayExchangeShippingFee =
    exchangeShippingFee > 0 && chargeExchangeShippingViaStripe;

  const hasInstantRepairFee =
    getBaseRepairFee(totalRepairFee) > 0 &&
    draftReturn.repairFeeCollectionStrategy ===
      RepairFeeCollectionStrategy.INSTANT;

  const hasExchangeReturn = isDraftingExchangeReturn(draftReturn);
  const isInstantProvision =
    draftReturn.provisionType === ProvisionType.Instant;

  const isReturnCreatedUpsellBilling =
    (draftReturn.upsellBillingTrigger ??
      UpsellBillingTrigger.ExchangeOrderCreated) ===
      UpsellBillingTrigger.ReturnCreated && totalPaymentOwed > 0;

  return (
    (isInstantProvision && hasExchangeReturn) ||
    needsToPayRegularShippingFee ||
    needsToPayExchangeShippingFee ||
    hasInstantRepairFee ||
    (isReturnCreatedUpsellBilling &&
      !(
        draftReturn.waiveOutstandingBalance && !draftReturn.keepFeesAndBonuses
      ) &&
      !isUpsellFeeWaived(draftReturn, calculations))
  );
}

export function isOutstandingBalanceWaived(params: {
  manuallyWaived: boolean;
  amount: number;
  currency: Currency;
}): boolean {
  const { manuallyWaived, amount, currency } = params;
  if (amount <= 0) {
    return false;
  }
  return manuallyWaived || shouldWaiveFee(amount, currency);
}

export function isUpsellFeeWaived(
  draftReturn: DraftReturn,
  calculations: ReturnTotalCalculations,
): boolean {
  const currency = draftReturn.currency || Currency.USD;
  const { totalShippingFee, exchangeShippingFee, totalPaymentOwed } =
    calculations;

  const isReturnCreatedUpsellBilling =
    (draftReturn.upsellBillingTrigger ??
      UpsellBillingTrigger.ExchangeOrderCreated) ===
      UpsellBillingTrigger.ReturnCreated && totalPaymentOwed > 0;

  if (!isReturnCreatedUpsellBilling) {
    return false;
  }

  const shippingFee = totalShippingFee - exchangeShippingFee;
  const upsellFeeAmount = Math.max(
    0,
    totalPaymentOwed - shippingFee - (exchangeShippingFee || 0),
  );

  return upsellFeeAmount > 0 && shouldWaiveFee(upsellFeeAmount, currency);
}
